// Seguimiento de pedidos — rol "administracion" (Marcela). SOLO LECTURA.
// Muestra los pedidos agrupados por estado (todo el ciclo) y un atajo a
// Facturación con contador de pendientes (hace de aviso). Al tocar un pedido
// abre un detalle de solo lectura (composición + cliente), sin acciones.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";

const ESTADO_META = {
  PENDIENTE_ASIGNAR: { label: "Pendiente de asignar", icon: "📥", color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  ASIGNADO:          { label: "Asignado",             icon: "👤", color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  EN_PREPARACION:    { label: "En preparación",       icon: "🔧", color: "#854d0e", bg: "#fefce8", border: "#fde047" },
  CON_ERROR:         { label: "Con error",            icon: "🛑", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  PREPARADO:         { label: "Preparado",            icon: "✅", color: "#15803d", bg: "#f0fdf4", border: "#86efac" },
  CONTROLADO:        { label: "Controlado",           icon: "🔍", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
  DESPACHADO:        { label: "Despachado",           icon: "🚚", color: "#185fa5", bg: "#eff6ff", border: "#bfdbfe" },
  ENTREGADO:         { label: "Entregado",            icon: "📦", color: "#15803d", bg: "#f0fdf4", border: "#86efac" },
};
const ORDEN = ["PENDIENTE_ASIGNAR", "ASIGNADO", "EN_PREPARACION", "CON_ERROR", "PREPARADO", "CONTROLADO", "DESPACHADO", "ENTREGADO"];

export default function SeguimientoAdmin() {
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [despachados, setDespachados] = useState([]);
  const [entregados, setEntregados] = useState([]);
  const [colapsados, setColapsados] = useState(() => new Set());

  useEffect(() => {
    const u1 = onSnapshot(collection(db, "pedidos"), s =>
      setPedidos(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(collection(db, "pedidos_despachados"), s =>
      setDespachados(s.docs.map(d => ({ id: d.id, ...d.data(), estado: "DESPACHADO" }))));
    const u3 = onSnapshot(collection(db, "pedidos_entregados"), s =>
      setEntregados(s.docs.map(d => ({ id: d.id, ...d.data(), estado: "ENTREGADO" }))));
    return () => { u1(); u2(); u3(); };
  }, []);

  // Un registro por pedido, en su estado más avanzado (entregado > despachado > activo).
  const todos = useMemo(() => {
    const map = new Map();
    for (const p of pedidos) if (!["ANULADO", "CANCELADO"].includes(p.estado)) map.set(p.id, p);
    for (const p of despachados) map.set(p.id, p);
    for (const p of entregados) map.set(p.id, p);
    return [...map.values()];
  }, [pedidos, despachados, entregados]);

  const porEstado = useMemo(() => {
    const out = {};
    for (const p of todos) {
      const e = p.estado || "PENDIENTE_ASIGNAR";
      (out[e] ||= []).push(p);
    }
    return out;
  }, [todos]);

  // Pendientes de facturar (para el atajo/aviso).
  const pendientesFacturar = useMemo(
    () => [...despachados, ...entregados].filter(p => p.facturado !== true).length,
    [despachados, entregados]
  );

  const toggle = (e) => setColapsados(prev => {
    const n = new Set(prev); n.has(e) ? n.delete(e) : n.add(e); return n;
  });

  return (
    <div style={{ padding: "16px", maxWidth: "760px", margin: "0 auto", paddingBottom: "40px" }}>
      <button onClick={() => navigate("/")} style={{ background: "none", border: "none", color: "#0f766e", fontWeight: 600, fontSize: "0.85rem", padding: 0, marginBottom: "12px", cursor: "pointer" }}>
        ← Volver
      </button>

      <h2 style={{ margin: "0 0 4px", fontSize: "1.2rem", fontWeight: 800 }}>👁️ Seguimiento de pedidos</h2>
      <p style={{ margin: "0 0 14px", fontSize: "0.85rem", color: "#64748b" }}>
        Estado de todos los pedidos (solo lectura). Tocá uno para ver su detalle.
      </p>

      {/* ── Atajo a Facturación (con aviso/contador) ── */}
      <button
        onClick={() => navigate("/administracion/facturacion")}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: "12px",
          padding: "14px 16px", borderRadius: "14px", cursor: "pointer", textAlign: "left",
          border: `1.5px solid ${pendientesFacturar > 0 ? "#fca5a5" : "#99f6e4"}`,
          background: pendientesFacturar > 0 ? "#fef2f2" : "#f0fdfa",
          marginBottom: "18px",
        }}
      >
        <span style={{ fontSize: "1.6rem", lineHeight: 1 }}>🧾</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: "0.95rem", color: pendientesFacturar > 0 ? "#b91c1c" : "#0f766e" }}>
            Ir a Facturación
          </div>
          <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
            {pendientesFacturar > 0
              ? `Tenés ${pendientesFacturar} pedido${pendientesFacturar !== 1 ? "s" : ""} pendiente${pendientesFacturar !== 1 ? "s" : ""} de facturar`
              : "No hay pedidos pendientes de facturar"}
          </div>
        </div>
        {pendientesFacturar > 0 && (
          <span style={{
            flexShrink: 0, minWidth: "28px", height: "28px", padding: "0 8px",
            borderRadius: "999px", background: "#dc2626", color: "#fff",
            fontSize: "0.9rem", fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {pendientesFacturar}
          </span>
        )}
        <span style={{ flexShrink: 0, color: pendientesFacturar > 0 ? "#b91c1c" : "#0f766e", fontWeight: 700 }}>→</span>
      </button>

      {/* ── Estados ── */}
      {ORDEN.filter(e => (porEstado[e] || []).length > 0).map(e => {
        const meta = ESTADO_META[e] || { label: e, icon: "📋", color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" };
        const items = porEstado[e];
        const abierto = !colapsados.has(e);
        return (
          <div key={e} style={{ marginBottom: "8px" }}>
            <button
              onClick={() => toggle(e)}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: "10px",
                padding: "12px 14px", cursor: "pointer", textAlign: "left",
                background: abierto ? meta.bg : "#fff",
                border: `1.5px solid ${abierto ? meta.border : "#e2e8f0"}`,
                borderRadius: abierto ? "12px 12px 0 0" : "12px",
              }}
            >
              <span style={{ fontSize: "1rem" }}>{meta.icon}</span>
              <span style={{ flex: 1, fontWeight: 700, fontSize: "0.88rem", color: meta.color }}>{meta.label}</span>
              <span style={{ background: meta.color + "22", color: meta.color, fontSize: "0.78rem", fontWeight: 700, padding: "2px 9px", borderRadius: "999px" }}>{items.length}</span>
              <span style={{ color: meta.color, fontSize: "0.85rem", transform: abierto ? "none" : "rotate(-90deg)" }}>▾</span>
            </button>
            {abierto && (
              <div style={{ border: `1.5px solid ${meta.border}`, borderTop: "none", borderRadius: "0 0 12px 12px", background: "#fff", padding: "6px" }}>
                {items
                  .slice()
                  .sort((a, b) => (b.numero || 0) - (a.numero || 0))
                  .map(p => (
                    <div
                      key={p.id}
                      onClick={() => navigate(`/administracion/seguimiento/${encodeURIComponent(p.id)}`)}
                      style={{ padding: "10px 12px", borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px", borderBottom: "1px solid #f1f5f9" }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: "0.86rem", color: "#0f172a" }}>#{p.numero || p.id}</div>
                        <div style={{ fontSize: "0.8rem", color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.cliente || "—"}</div>
                      </div>
                      {p.metodoEntrega && (
                        <span style={{ fontSize: "0.64rem", fontWeight: 700, color: "#475569", background: "#f1f5f9", padding: "2px 7px", borderRadius: "999px", flexShrink: 0 }}>{p.metodoEntrega}</span>
                      )}
                      <span style={{ color: "#cbd5e1", flexShrink: 0 }}>›</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        );
      })}

      {todos.length === 0 && (
        <p style={{ color: "#94a3b8", fontSize: "0.9rem", textAlign: "center", padding: "30px 0" }}>No hay pedidos para mostrar.</p>
      )}
    </div>
  );
}
